import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { createDataSource } from '../data/createDataSource';
import { DEFAULT_FILTERS, type AnalyticsDataSource, type DataScope, type DateRange, type QueryFilters, type Segment } from '../types/analytics';
import { compareRange as buildCompareRange, rangeFromPreset, type CompareMode } from '../utils/dateRange';
import { useAuth } from '../auth/AuthProvider';

interface DashboardContextValue {
  range: DateRange;
  setRange: (range: DateRange) => void;
  dataSource: AnalyticsDataSource;
  filters: QueryFilters;
  setFilters: (filters: QueryFilters) => void;
  compare: CompareMode;
  setCompare: (mode: CompareMode) => void;
  /** The range being compared against, or null when comparison is off. */
  comparisonRange: DateRange | null;
}

const DashboardContext = createContext<DashboardContextValue | null>(null);

export const PREFERENCES_KEY = 'aruha-analytics-preferences';

interface Preferences { filters: QueryFilters; compare: CompareMode }

const scopes: DataScope[] = ['filtered', 'raw'];
const segments: Segment[] = ['all', 'guest', 'registered'];
const compareModes: CompareMode[] = ['off', 'previous', 'week'];

/** Reads the saved viewer preferences. Storage can be missing, blocked or corrupt, so every failure falls back to defaults. */
export function loadPreferences(): Preferences {
  const fallback: Preferences = { filters: DEFAULT_FILTERS, compare: 'off' };
  try {
    const raw = window.localStorage.getItem(PREFERENCES_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<{ scope: DataScope; segment: Segment; compare: CompareMode }>;
    return {
      filters: {
        scope: scopes.includes(parsed.scope as DataScope) ? parsed.scope as DataScope : DEFAULT_FILTERS.scope,
        segment: segments.includes(parsed.segment as Segment) ? parsed.segment as Segment : DEFAULT_FILTERS.segment,
      },
      compare: compareModes.includes(parsed.compare as CompareMode) ? parsed.compare as CompareMode : 'off',
    };
  } catch {
    return fallback;
  }
}

function savePreferences(filters: QueryFilters, compare: CompareMode): void {
  try {
    window.localStorage.setItem(PREFERENCES_KEY, JSON.stringify({ scope: filters.scope, segment: filters.segment, compare }));
  } catch {
    // Private windows and blocked storage are fine: the choice simply does not outlive the page.
  }
}

export function DashboardProvider({ children, source }: { children: ReactNode; source?: AnalyticsDataSource }) {
  const [range, setRange] = useState<DateRange>(() => rangeFromPreset('30d'));
  const [filters, setFiltersState] = useState<QueryFilters>(() => loadPreferences().filters);
  const [compare, setCompareState] = useState<CompareMode>(() => loadPreferences().compare);
  const { getAccessToken } = useAuth();
  const dataSource = useMemo(() => source ?? createDataSource(undefined, getAccessToken), [getAccessToken, source]);
  const setFilters = useCallback((next: QueryFilters) => { setFiltersState(next); savePreferences(next, compare); }, [compare]);
  const setCompare = useCallback((mode: CompareMode) => { setCompareState(mode); savePreferences(filters, mode); }, [filters]);
  const comparisonRange = useMemo(() => buildCompareRange(range, compare), [range, compare]);
  return <DashboardContext.Provider value={{ range, setRange, dataSource, filters, setFilters, compare, setCompare, comparisonRange }}>{children}</DashboardContext.Provider>;
}

export function useDashboard() {
  const context = useContext(DashboardContext);
  if (!context) throw new Error('useDashboard must be used inside DashboardProvider');
  return context;
}
